import type IItem from 'types/product_delivery';
import type { Price } from 'api/customer/products_prices.api';
import { updateAllPrice, updateCustomerProductAvailable } from 'api/customer/products_prices.api';
import type { ImportAdapter, ImportColumnDef, ImportCommitOutcome, ImportRowAnalysis, ImportRowPreview } from 'ui-component/import-wizard';
import { getCell, toFloat, toInt } from 'ui-component/import-wizard';
import { findPriceRow, normBarcode, PRICE_COL } from './importCustomerPricesFromXlsxUi';
import { roundDecimals } from 'fromKotlin/nkutils';

export const CUSTOMER_PRICES_IMPORT_COLUMNS: ImportColumnDef[] = [
    { key: 'id', label: PRICE_COL.id, type: 'number', hint: 'מזהה מוצר פנימי' },
    { key: 'externalId', label: PRICE_COL.externalId, type: 'string' },
    { key: 'barcode', label: PRICE_COL.barcode, type: 'string' },
    { key: 'name', label: PRICE_COL.name, type: 'string', hint: 'לזיהוי בלבד; לא נדרש בשביל ההתאמה' },
    { key: 'price', label: PRICE_COL.price, type: 'number' },
    { key: 'discount', label: PRICE_COL.discount, type: 'number' }
];

export const CUSTOMER_PRICES_FIELD_LABELS: Record<string, string> = {
    totalPrice: 'מחיר',
    discount: 'הנחה'
};

export interface ImportCustomerPricesContext {
    cid: string;
    dateStr: string;
    toDateStr?: string;
    unique: boolean;
}

function analyzePriceRow(row: unknown[], cols: Record<string, number>, ctx: { currentData: IItem[] }): ImportRowAnalysis<IItem> {
    const idStr = getCell(row, cols, PRICE_COL.id);
    const idNum = toInt(idStr);
    const ext = getCell(row, cols, PRICE_COL.externalId);
    const bc = getCell(row, cols, PRICE_COL.barcode);
    const nameHint = getCell(row, cols, PRICE_COL.name);
    const priceStr = getCell(row, cols, PRICE_COL.price);
    const discStr = getCell(row, cols, PRICE_COL.discount);

    const hasId = idNum != null && idNum > 0;
    const hasExt = !!ext && ext.trim() !== '';
    const hasBc = !!bc && bc.trim() !== '';

    if (!hasId && !hasExt && !hasBc) {
        return { action: 'invalid', identity: {}, patch: {}, label: nameHint, errors: ['חסר מזהה / מזהה חיצוני / ברקוד'], warnings: [] };
    }

    const product = findPriceRow(ctx.currentData, hasId ? idNum : undefined, hasExt ? ext : undefined, hasBc ? bc : undefined);
    if (!product) {
        return {
            action: 'invalid',
            identity: { idNum: hasId ? idNum : undefined, externalIdStr: hasExt ? ext : undefined, extraKey: hasBc ? normBarcode(bc) : undefined },
            patch: {},
            label: nameHint || bc,
            errors: [`מוצר לא נמצא בטבלת המחירים הנוכחית${nameHint ? `: ${nameHint}` : ''}`],
            warnings: []
        };
    }

    // Use the resolved product's own id as the identity key so two rows that identify the
    // same product via different columns (e.g. one by id, one by barcode) are still caught
    // as an in-file duplicate instead of silently applying two conflicting price updates.
    const identity = { idNum: product.id };

    const pFloat = toFloat(priceStr);
    const dFloat = toFloat(discStr);
    if (pFloat === undefined && dFloat === undefined) {
        return { action: 'invalid', identity, existing: product, patch: {}, label: product.name, errors: ['חסר מחיר והנחה'], warnings: [] };
    }

    const price = pFloat !== undefined ? roundDecimals(Math.max(0, pFloat), 4) : roundDecimals(Number(product.totalPrice ?? 0), 4);
    const discount = dFloat !== undefined ? roundDecimals(dFloat, 4) : roundDecimals(Number(product.discount ?? 0), 4);

    return {
        action: 'update',
        identity,
        label: product.name,
        existing: product,
        patch: { totalPrice: price, discount },
        errors: [],
        warnings: []
    };
}

export function buildCustomerPricesImportAdapter(ctx: ImportCustomerPricesContext): ImportAdapter<IItem> {
    const clientNum = Number(ctx.cid);
    return {
        entityLabel: 'מוצר',
        entityLabelPlural: 'מוצרים',
        title: 'עדכון מחירים מקובץ אקסל',
        columns: CUSTOMER_PRICES_IMPORT_COLUMNS,
        fieldLabels: CUSTOMER_PRICES_FIELD_LABELS,
        maxRows: 10000,
        instructions: [
            'קובץ זה מעדכן מחירים והנחות למוצרים שכבר קיימים ברשימת המחירים של הלקוח; הוא לא יוצר מוצרים חדשים.',
            'התאמת מוצר נעשית לפי מזהה פנימי, מזהה חיצוני, או ברקוד — בסדר עדיפות זה.'
        ],
        reviewOptions: [
            {
                key: 'updateAvailable',
                label: 'עדכן גם זמינות — סמן את המוצרים שנמצאו כזמינים להזמנה ללקוח זה'
            }
        ],
        analyzeRow: analyzePriceRow,
        commitBatch: async (rows: ImportRowPreview<IItem>[], commitCtx): Promise<ImportCommitOutcome<IItem>[]> => {
            const priceRows = rows.filter((r) => r.selected);
            const updates: Price[] = priceRows.map((r) => {
                const product = r.existing as IItem;
                const master = (product as IItem & { master?: number }).master;
                return {
                    id: clientNum,
                    product_id: product.id,
                    price: r.patch.totalPrice as number,
                    discount: r.patch.discount as number,
                    master: master != null && master !== -1 ? master : -1
                };
            });
            try {
                if (updates.length) {
                    await updateAllPrice(updates, ctx.dateStr, ctx.toDateStr, false, undefined, undefined, ctx.unique);
                }
            } catch (e) {
                const message = (e as Error)?.message || String(e);
                return rows.map(() => ({ ok: false, message }));
            }
            if (commitCtx?.options?.updateAvailable) {
                const found = (commitCtx.allRows ?? rows).filter((r) => {
                    if (r.action === 'invalid' || r.action === 'duplicate') return false;
                    const id = Number((r.existing as IItem | undefined)?.id);
                    return Number.isFinite(id) && id > 0;
                });
                const productIds = Array.from(new Set(found.map((r) => Number((r.existing as IItem).id))));
                if (productIds.length) {
                    try {
                        await updateCustomerProductAvailable([clientNum], productIds, undefined, true);
                    } catch (e) {
                        const message = (e as Error)?.message || String(e);
                        return rows.map((r) => ({
                            ok: updates.length > 0,
                            message: updates.length
                                ? `${r.label ? `המחיר עודכן: ${r.label}` : 'המחיר עודכן'}, אך עדכון הזמינות נכשל: ${message}`
                                : `עדכון הזמינות נכשל: ${message}`
                        }));
                    }
                    return rows.map((r) => ({
                        ok: true,
                        message: r.label
                            ? (updates.length ? `עודכן וסומן כזמין: ${r.label}` : `סומן כזמין: ${r.label}`)
                            : (updates.length ? 'עודכן וסומן כזמין' : 'סומן כזמין')
                    }));
                }
            }
            if (!updates.length) {
                return rows.map(() => ({ ok: false, message: 'אין שורות לביצוע' }));
            }
            return rows.map((r) => ({ ok: true, message: r.label ? `עודכן: ${r.label}` : 'עודכן' }));
        }
    };
}
